import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map, tap } from 'rxjs/operators';
import { ConstantsFordProtect } from "src/app/models/constants/constants-ford-protect";
import { OperationCode } from "src/app/models/enum/operation-code";
import { Product } from "src/app/models/product/product";
import { ProductActive } from "src/app/models/product/product-active";
import { ProductCreateUpdateDto } from "src/app/models/product/product-create-update";
import { ProductVisit } from "src/app/models/product/product-visit";
import { environment } from "src/environments/environment";

type ProductByIdResponse = {
  id: number;
  name: string;
  country: string;
  productStatus: string | number;
  description: string;
  productCode: string;
  code?: string;
  visit?: number;
  invoiceMonths?: number;
  months?: number;
  kilometers?: number;
  accountCode?: string;
  accountNumber: string;
  actionCode: string;
  coverageCode: string;
  insuranceCode: string;
  createDate?: Date;
  productDetails?: string;
  details?: string;
  planYear: string;
  updateUser?: string;
  rateCurveId?: number;
};

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json'
  })
};

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  private apiUrl = environment.apiUrl;
  private country = ConstantsFordProtect.BRA;
  private operation = OperationCode.CAR;

  /**
   * Tracks the ID of the most recently edited product.
   * view-plans uses this to detect when to refresh the search results.
   */
  private lastEditedProductId: number | null = null;

  /**
   * Caches the last countries list used in view-plans for Product search.
   * When user returns from editing, we re-apply the same search to get fresh data.
   */
  private lastSearchCountries: string[] | null = null;

  constructor(private httpClient: HttpClient) {
  }

  /**
   * Returns the ID of the most recently edited product (if any).
   * Clears the value after retrieval (one-time check).
   */
  getAndClearLastEditedProductId(): number | null {
    const id = this.lastEditedProductId;
    this.lastEditedProductId = null;
    return id;
  }

  /**
   * Saves the countries list used in view-plans for Product search.
   * This allows automatic re-search when user returns from editing.
   */
  cacheSearchCountries(countries: string[]): void {
    this.lastSearchCountries = countries ? [...countries] : null;
  }

  /**
   * Returns the cached countries list (if any).
   */
  getCachedSearchCountries(): string[] | null {
    return this.lastSearchCountries;
  }

  getProducts(): Observable<Product[]> {
    return this.httpClient.get<Product[]>(`${this.apiUrl}/api/products/${this.country}/${this.operation}`)
      .pipe(
        map((response: Product[]) => {
          return response;
        })
      );
  }

  getActivesProductsByCountry(country: string, operation: string, statusProduct: number): Observable<ProductActive[]> {
    return this.httpClient.get<ProductActive[]>(`${this.apiUrl}/api/products/active/${country}/${operation}/${statusProduct}`)
      .pipe(
        map((response: ProductActive[]) => {
          return response;
        })
      );
  }

  getActivesProductsAndNotRestrictedByCountry(country: string): Observable<ProductActive[]> {
    return this.httpClient.get<ProductActive[]>(`${this.apiUrl}/api/products/active/restricted/${country}`)
      .pipe(
        map((response: ProductActive[]) => {
          return response;
        })
      );
  }

  getProductActiveCodeCountries(countries: string[]): Observable<ProductActive[]> {
    return this.httpClient.post(`${this.apiUrl}/api/products/active/code/countries`, countries, httpOptions)
      .pipe(
        map((response: ProductActive[]) => {
          return response;
        })
      );
  }

  getProductsByCountry(countries: string[]): Observable<ProductVisit[]> {
    return this.httpClient.post<ProductVisit[]>(`${this.apiUrl}/api/products/countries`, countries, httpOptions)
      .pipe(
        map((response: ProductVisit[]) => {
          return response;
        })
      );
  }

  getProductsByCountryForProductPolicies(country: string): Observable<ProductVisit[]> {
    return this.httpClient.get<ProductVisit[]>(`${this.apiUrl}/api/products/${country}`)
    .pipe(
      map((response: ProductVisit[]) => {
        return response;
      })
    );
}

  getAllProducts(): Observable<ProductVisit[]> {
    return this.httpClient.get(`${this.apiUrl}/api/products`)
      .pipe(
        map((response: ProductVisit[]) => {
          return response;
        })
      );
  }

  getProductActiveCodeCountry(country: string): Observable<ProductActive[]> {
    return this.httpClient.get<ProductActive[]>(`${this.apiUrl}/api/products/active/code/${country}`)
      .pipe(
        map((response: ProductActive[]) => {
          return response;
        })
      );
  }

  createProduct(cadProduct: ProductCreateUpdateDto): Observable<ProductCreateUpdateDto> {
    return this.httpClient.post<ProductCreateUpdateDto>(`${this.apiUrl}/api/products/create/product`, cadProduct);
  }

  updateProduct(editedProduct: ProductCreateUpdateDto): Observable<ProductCreateUpdateDto> {
    return this.httpClient.put<ProductCreateUpdateDto>(`${this.apiUrl}/api/products/product`, editedProduct)
      .pipe(
        tap((_updatedProduct) => {
          // Track that this product was just edited
          if (editedProduct && typeof editedProduct === 'object' && 'id' in editedProduct) {
            this.lastEditedProductId = (editedProduct as unknown as Record<string, unknown>).id as number | null;
          }
        })
      );
  }

  getProductById(id: number): Observable<ProductByIdResponse> {
    return this.httpClient.get<ProductByIdResponse>(`${this.apiUrl}/api/products/id/${id}`)
      .pipe(
        map((response: ProductByIdResponse) => {
          return response;
        })
      );
  }

  

}
